import { Link, useRouterState } from "@tanstack/react-router";
import { ArrowLeft, Lock } from "lucide-react";
import { useState } from "react";
import { ROUTES } from "@/app/routes";
import { useAuth } from "@/shared/auth/auth.context";
import { canDo, Permission } from "@/shared/auth/permissions";
import { usePayrollNavSetupFirst } from "@/shared/components/layout/payroll-nav-order";
import {
  isPayrollDestinationActive,
  type PayrollDestination,
  payrollSidebarGroups,
  resolvePayrollRunsView,
} from "@/shared/components/layout/payroll-sidebar-model";
import { findNavItem } from "@/shared/components/layout/sidebar-nav.config";
import { keepItem } from "@/shared/components/layout/sidebar-nav.gate";
import { Button } from "@/shared/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { UpgradeDialog } from "@/shared/entitlements/components/upgrade-dialog";
import { useEntitlements } from "@/shared/entitlements/entitlements.context";
import { resolveFeatureConfirmed } from "@/shared/entitlements/hooks/use-feature-enabled";
import { useActiveWorkspace } from "@/shared/hooks/use-active-workspace";
import { useSidebarCollapsed } from "@/shared/hooks/use-sidebar-collapsed";
import { cn } from "@/shared/lib/cn";

export function PayrollSidebarNav() {
  const { workspace } = useActiveWorkspace();
  const { collapsed } = useSidebarCollapsed();
  const { location } = useRouterState();
  const auth = useAuth();
  const { features } = useEntitlements();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const accountRole = auth.user?.accountRole ?? null;
  const workspaceRoles = auth.memberships
    .filter((membership) => membership.workspaceId === workspace.id)
    .map((membership) => membership.role);
  const setupRequired = usePayrollNavSetupFirst(
    workspace.id,
    canDo(Permission.PAYROLL_CONFIG_VIEW, { accountRole, workspaceRoles }) &&
      resolveFeatureConfirmed(features, "payroll"),
  );
  const catalog = findNavItem("Payroll");
  const groups = payrollSidebarGroups(
    catalog ? keepItem(catalog, accountRole, workspaceRoles, features) : null,
  );
  const view = resolvePayrollRunsView(location.search);

  return (
    <div className="flex min-h-0 flex-col">
      <div className={cn("border-b border-border pb-3", collapsed ? "px-2" : "px-3")}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              asChild
              variant="ghost"
              className={cn(
                "h-11 w-full justify-start gap-2 text-text-secondary md:h-9",
                collapsed && "justify-center px-0",
              )}
            >
              <Link
                to={ROUTES.workspace.home.path}
                params={{ workspaceSlug: workspace.slug }}
                aria-label="Back to workspace"
              >
                <ArrowLeft className="size-4 shrink-0" aria-hidden="true" />
                {collapsed ? null : <span>Back to workspace</span>}
              </Link>
            </Button>
          </TooltipTrigger>
          {collapsed ? <TooltipContent side="right">Back to workspace</TooltipContent> : null}
        </Tooltip>
        {collapsed ? null : (
          <h2 className="px-2.5 pt-3 text-base font-semibold text-foreground">Payroll</h2>
        )}
      </div>
      <nav
        aria-label="Payroll"
        className={cn(
          "min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-4 [scrollbar-width:thin]",
          collapsed ? "px-2" : "px-3",
        )}
      >
        {groups.map((group) => (
          <section key={group.title} aria-label={group.title || "Run payroll"} className="pt-4">
            {group.title && !collapsed ? (
              <h3 className="px-2.5 pb-1.5 text-xs font-medium text-text-tertiary">
                {group.title}
              </h3>
            ) : null}
            <div className="flex flex-col gap-0.5">
              {group.items.map((item) => (
                <PayrollNavLink
                  key={item.label}
                  item={item}
                  slug={workspace.slug}
                  runFilters={
                    location.pathname ===
                    ROUTES.workspace.payrollRuns.path.replace("$workspaceSlug", workspace.slug)
                      ? {
                          q: typeof location.search.q === "string" ? location.search.q : undefined,
                          status:
                            typeof location.search.status === "string"
                              ? location.search.status
                              : undefined,
                        }
                      : {}
                  }
                  collapsed={collapsed}
                  active={isPayrollDestinationActive(item, location.pathname, workspace.slug, view)}
                  setupRequired={setupRequired && item.to === ROUTES.workspace.payrollSetup.path}
                  onLockedClick={() => setUpgradeOpen(true)}
                />
              ))}
            </div>
          </section>
        ))}
      </nav>
      <UpgradeDialog open={upgradeOpen} feature="payroll" onOpenChange={setUpgradeOpen} />
    </div>
  );
}

function PayrollNavLink({
  item,
  slug,
  collapsed,
  active,
  setupRequired,
  onLockedClick,
  runFilters,
}: {
  item: PayrollDestination;
  runFilters: { q?: string; status?: string };
  slug: string;
  collapsed: boolean;
  active: boolean;
  setupRequired: boolean;
  onLockedClick: () => void;
}) {
  const Icon = item.icon;
  const label = `${item.label}${setupRequired ? ", setup required" : ""}${item.locked ? " (locked, opens upgrade options)" : ""}`;
  const content = (
    <>
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      {collapsed ? null : (
        <span className="min-w-0 flex-1 text-left">
          <span className="block truncate">{item.label}</span>
          {setupRequired ? (
            <span className="block text-xs font-normal text-text-secondary">Setup required</span>
          ) : null}
        </span>
      )}
      {item.locked ? (
        <Lock
          className={cn(
            "size-3 shrink-0",
            collapsed && "absolute bottom-1 right-1 rounded-sm bg-surface-subtle",
          )}
          aria-hidden="true"
        />
      ) : null}
    </>
  );
  const className = cn(
    "relative h-auto min-h-11 w-full justify-start gap-2.5 px-2.5 py-2 text-sm font-medium text-text-secondary md:min-h-9 md:py-1.5",
    collapsed && "justify-center px-0",
    active && !item.locked && "bg-primary/10 text-text-brand hover:bg-primary/15",
    item.locked && "text-text-tertiary",
  );
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {item.locked ? (
          <Button
            variant="ghost"
            aria-label={label}
            aria-disabled="true"
            className={className}
            onClick={onLockedClick}
          >
            {content}
          </Button>
        ) : (
          <Button asChild variant="ghost" className={className}>
            <Link
              // reason: destinations come from the typed route catalog, resolved for this workspace.
              to={item.to.replace("$workspaceSlug", slug) as never}
              // reason: the runtime destination is a catalog route; only run destinations
              // receive search fields, which their route schema validates on arrival.
              search={
                (item.view
                  ? { ...runFilters, ...(item.view !== "runs" ? { view: item.view } : {}) }
                  : {}) as never
              }
              aria-label={label}
              aria-current={active ? "page" : undefined}
            >
              {content}
            </Link>
          </Button>
        )}
      </TooltipTrigger>
      {collapsed ? <TooltipContent side="right">{label}</TooltipContent> : null}
    </Tooltip>
  );
}
